/**
 * Marcador de segmento na URL do `/_serverFn`, para que o CDN da Cloudflare
 * possa cachear sem invocar o Worker.
 *
 * O problema: a CF chaveia pela URL crua. O Worker chaveia por uma Request
 * SINTÉTICA que recebe `__seg`/`__v`/`__bot`/`__fetch` (buildCacheKey em
 * @decocms/tanstack/sdk/workerEntry) — params que o CDN nunca vê. Por isso o
 * framework carimba `CDN-Cache-Control: no-store` em toda resposta pública
 * (workerEntry.ts:2081, default da opção em :864), e 100% do tráfego sai com
 * `cf-cache-status: BYPASS`.
 *
 * A solução: colocar o segmento na própria URL. Aí a chave do CDN volta a
 * equivaler à do Worker e liberar o cache é seguro.
 *
 * Esta é a ÚNICA definição do formato do token. O cliente (src/start.ts) a usa
 * para montar o marcador; o worker (src/worker-entry.ts) a usa para recalcular
 * e comparar. Mesma função nos dois lados, então não há como divergirem.
 */

/** `__d` é reservado: workerEntry.ts:1481 usa `?__d=` como flag de debug de OTel. */
export const CSEG_PARAM = "__cseg";

/** Subconjunto de SegmentKey que interessa aqui. */
export interface CdnSegment {
  device: string;
  loggedIn?: boolean;
  salesChannel?: string;
  regionId?: string;
}

/**
 * Token do segmento, ou `null` quando esta requisição não pode ser cacheada
 * no CDN.
 *
 * Devolve `null` — e portanto mantém o `no-store` — quando:
 *
 * - há qualquer personalização além do device (`loggedIn`, `salesChannel`,
 *   `regionId`). Só o device é seguro de expor na URL; o resto precisa
 *   continuar resolvendo no Worker.
 * - não há build hash, ou ele é `"dev"`. O build entra no token porque o
 *   deploy NÃO purga o CDN (a purga do framework limpa `caches.default`), então
 *   a URL precisa mudar sozinha quando o bundle muda.
 *
 * GUARDA — antes de ligar geo (`geoCacheKey`) ou A/B (`withABTesting`): a chave
 * do Worker ganha `__cf_geo`/`__abf` (workerEntry.ts:1211,1250) mas este token
 * NÃO os codifica. Com qualquer um dos dois ativo, o CDN (que chaveia só por
 * URL + este token) fica mais grosso que o Worker e serve resposta de um
 * geo/coorte para outro. Nesse cenário, ou o token passa a incluir o eixo novo,
 * ou a liberação de CDN em worker-entry.ts (cdnCacheableServerFn) deve ser
 * desligada. Hoje ambos são inertes (sem matcher de location, A/B comentado).
 */
export function segmentToken(seg: CdnSegment, buildHash: string | undefined): string | null {
  if (seg.loggedIn || seg.salesChannel || seg.regionId) return null;
  if (!buildHash || buildHash === "dev") return null;
  if (!seg.device) return null;
  return `${seg.device}.${buildHash}`;
}
